import { useNavigate } from 'react-router-dom';
import { Button } from '@portal_components/components/Button';
import { DatePicker } from '@portal_components/components/DatePicker';
import { TableItem } from '@portal_components/components/TableItem';
import { TextInput } from '@portal_components/components/TextInput';
import { Group, Contract, EPageViewType } from '../../types';
import { ContractView } from '../contract/ContractView';
import { GroupView } from '../group/GroupView';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { EnvironmentVariables } from '../../EnvironmentVariables';
import { useUserInfo } from '../../hooks/useUserInfo';
import { EUserRole } from '@portal_components/types';

interface CustomerSearchResultProps {
  group: Group;
  contracts: Partial<Contract>[];
  expandedContractIndex: number | null;
  setExpandedContractIndex: (index: number | null) => void;
}

export const CustomerSearchResult = ({
  group,
  contracts,
  expandedContractIndex,
  setExpandedContractIndex
}: CustomerSearchResultProps) => {
  const navigate = useNavigate();
  const { userInfo } = useUserInfo();
  const {
    standingData: { standingDataContractTypeList }
  } = useContractStandingData();

  const handleViewSitesInPortfolioPortal = (contractId: string) => () => {
    if (!group.customerGroupId) return;
    const searchParams = new URLSearchParams({
      contractId: contractId,
      customerGroupId: group.customerGroupId
    });
    window.open(`${EnvironmentVariables.PortfolioPortalUrl}/search?${searchParams.toString()}`, '_blank');
  };

  const handleEditGroupButtonClick = () => {
    navigate(`/editGroup/${group.customerGroupId}`);
  };

  const handleEditContractButtonClicked = (contractId?: string) => () => {
    navigate(`/editContract/${group.customerGroupId}/${contractId}`);
  };

  const handleRenewContractButtonClicked = (contractId?: string) => () => {
    navigate(`/renewContract/${group.customerGroupId}/${contractId}`);
  };

  const handleAddContractButtonClicked = () => {
    navigate(`/addContract/${group.customerGroupId}`);
  };

  const renderContractListing = (contract: Partial<Contract>, index: number) => {
    const handleExpandClicked = () => {
      if (expandedContractIndex === index) {
        setExpandedContractIndex(null);
      } else {
        setExpandedContractIndex(index);
      }
    };
    if (!contract.contractId) return null;
    return (
      <div
        className="pt-2"
        key={`CustomerSearchResultContract-${group.customerGroupId}-${contract.contractId}`}
      >
        <TableItem readonly onExpandClicked={handleExpandClicked} expanded={expandedContractIndex === index}>
          <TextInput title="Contract" initialValue={contract.contractId} readonly hideErrorControl />
          <TextInput
            title="Contract Type"
            initialValue={
              standingDataContractTypeList.find(({ value }) => value == contract.contractType)?.title ?? ''
            }
            readonly
            hideErrorControl
          />
          <DatePicker
            title="Contract Start"
            initialValue={contract.contractStartDate}
            readonly
            onDateSelected={() => {
              return;
            }}
            hideErrorControl
          />
          <DatePicker
            title="Contract End"
            initialValue={contract.contractEndDate}
            readonly
            onDateSelected={() => {
              return;
            }}
            hideErrorControl
          />
        </TableItem>
        {expandedContractIndex === index && (
          <>
            <div className="pb-2 flex flex-row justify-end">
              <div
                className="text-interactive-coral hover:text-interactive-coral-db-70 text-sm cursor-pointer"
                onClick={handleViewSitesInPortfolioPortal(contract.contractId)}
              >
                View sites in Portfolio Portal
              </div>
              <img src="/externalLinkButtonActive.svg" alt="external-link" className="pl-1 w-4 h-4" />
            </div>
            <ContractView viewType={EPageViewType.VIEW} group={group} contract={contract} />
            {userInfo && userInfo.role !== EUserRole.READONLY_USER && (
              <div className="flex flex-row justify-end pb-4 pr-4 gap-4">
                <Button
                  text="Renew Contract"
                  primary={false}
                  onClick={handleRenewContractButtonClicked(contract.contractId)}
                />
                <Button
                  text="Edit Contract"
                  primary
                  onClick={handleEditContractButtonClicked(contract.contractId)}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <GroupView viewType={EPageViewType.VIEW} group={group} />
      {userInfo && userInfo.role !== EUserRole.READONLY_USER && (
        <div className="flex flex-row justify-end">
          <div className="pr-4">
            <Button text="Edit Customer" primary onClick={handleEditGroupButtonClick} />
          </div>
        </div>
      )}
      <div className="mt-8 flex flex-row justify-between items-center border-b-[1px] border-secondary-deep-blue-80 mb-4">
        <h2 className="text-2xl font-bold pl-4">Contracts</h2>
        {userInfo && userInfo.role !== EUserRole.READONLY_USER && (
          <div className="flex flex-row justify-end pr-4 py-3">
            <Button text="Add Contract" primary onClick={handleAddContractButtonClicked} />
          </div>
        )}
      </div>
      {contracts.length > 0 && group && contracts.map(renderContractListing)}
    </div>
  );
};
